import { useEffect, useMemo } from 'react';
import { Dropdown, InlineLoading } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { capitalize, debounce } from 'lodash-es';
import {
  AliasOption,
  aliasedOption,
  aliasedSearchText,
  aliasOptionProps,
} from 'components/alias-pills';
import { FreeTextComboBox } from 'components/free-text-combobox';
import * as sharedAtoms from './atoms';
import type { FetchGeometryAtom, ScenarioPickerForm } from './types';
import styles from './scenario-area-configs.module.scss';

/**
 * The atoms the unit fields read and write. Each scenario form keeps its own copy of them, so the
 * form passes its own; the shared ones are the default.
 */
export type UnitTypeFieldsAtoms = Pick<
  typeof sharedAtoms,
  | 'unitTypesLoadableAtom'
  | 'unitSearchLoadableAtom'
  | 'unitTypeAtom'
  | 'unitQueryAtom'
  | 'areaGeometriesAtom'
  | 'toastAtom'
> & { fetchGeometryLoadableAtom: FetchGeometryAtom<'unit'> };

export interface UnitTypeFieldsProps {
  form: ScenarioPickerForm;
  areaIndex: number;
  atoms?: UnitTypeFieldsAtoms;
}

type PickedItem = { value?: string; id?: number };
type AreaEntry = { id?: number; unitType?: string };

/**
 * Unit-type fields: unit type dropdown + unit name ComboBox with typeahead, shared by every scenario
 * form that picks a unit (the options show each unit's tags and a typed tag finds the unit).
 * @param {UnitTypeFieldsProps} props - The form, the area index and the atoms to use
 * @return {JSX.Element} The unit type fields
 */
export function UnitTypeFields({
  form,
  areaIndex,
  atoms = sharedAtoms,
}: UnitTypeFieldsProps): JSX.Element {
  const prefix = `areas[${areaIndex}]` as const;
  const unitTypesState = useAtomValue(atoms.unitTypesLoadableAtom);
  const unitSearchState = useAtomValue(atoms.unitSearchLoadableAtom);
  const setUnitType = useSetAtom(atoms.unitTypeAtom);
  const setUnitQuery = useSetAtom(atoms.unitQueryAtom);
  const fetchGeometry = useSetAtom(atoms.fetchGeometryLoadableAtom);
  const setGeometries = useSetAtom(atoms.areaGeometriesAtom);
  const setToast = useSetAtom(atoms.toastAtom);

  useEffect(() => {
    if (unitSearchState.state === 'hasError') {
      setToast({ message: 'Failed to search units', type: 'error' });
    }
  }, [unitSearchState.state, setToast]);

  const unitTypeItems =
    unitTypesState.state === 'hasData' && unitTypesState.data
      ? unitTypesState.data.unit_types
      : [];

  const debouncedUnitQuery = useMemo(
    () => debounce((text: string) => setUnitQuery(text), 350),
    [setUnitQuery]
  );

  useEffect(() => {
    return () => debouncedUnitQuery.cancel();
  }, [debouncedUnitQuery]);

  const unitItems = useMemo(() => {
    if (unitSearchState.state !== 'hasData' || !unitSearchState.data) return [];
    return unitSearchState.data.results.map(aliasedOption);
  }, [unitSearchState]);

  /**
   * Drops the geometry shown for an entry whose area or unit was cleared.
   * @param {number} entryId - The form entry
   * @return {void}
   */
  const forgetGeometry = (entryId: number) =>
    setGeometries((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });

  return (
    <div className={styles.formGrid}>
      {unitTypesState.state === 'loading' ? (
        <InlineLoading description="Loading unit types..." />
      ) : (
        <form.Field name={`${prefix}.unitType`}>
          {(field) => (
            <Dropdown
              id={`unit-type-${areaIndex}`}
              titleText="Unit Type (required)"
              label="Choose an option"
              size="md"
              light
              items={unitTypeItems}
              selectedItem={(field.state.value as string) || null}
              itemToString={(item: string) => (item ? capitalize(item) : '')}
              onChange={({ selectedItem }: { selectedItem: string }) => {
                const entryId = form.getFieldValue(`${prefix}.id`) as
                  number | undefined;
                field.handleChange(selectedItem || '');
                form.setFieldValue(`${prefix}.unitName`, '');
                form.setFieldValue(`${prefix}.configurableId`, null);
                setUnitType(selectedItem || '');
                setUnitQuery(undefined);
                if (entryId) forgetGeometry(entryId);
              }}
            />
          )}
        </form.Field>
      )}

      <form.Subscribe
        selector={(state) => {
          const area = (state.values as { areas: AreaEntry[] }).areas[
            areaIndex
          ];
          return { entryId: area?.id, unitType: area?.unitType };
        }}
      >
        {({ entryId, unitType }) => (
          <div
            className={styles.typeaheadWrapper}
            onFocus={() => {
              if (
                unitType &&
                unitSearchState.state !== 'loading' &&
                !unitItems.length
              ) {
                setUnitType(unitType);
                setUnitQuery('');
              }
            }}
          >
            <form.Field name={`${prefix}.unitName`}>
              {(field) => (
                <FreeTextComboBox
                  id={`unit-name-${areaIndex}`}
                  itemToElement={(item) => (
                    <AliasOption {...aliasOptionProps(item)} />
                  )}
                  itemToSearchText={aliasedSearchText}
                  titleText="Unit Name (required)"
                  placeholder="Search units..."
                  items={unitItems}
                  selectedItem={
                    field.state.value
                      ? {
                          value: field.state.value as string,
                          label: field.state.value as string,
                        }
                      : null
                  }
                  itemToString={(item) =>
                    (item?.label as string | undefined) ?? ''
                  }
                  onChange={({ selectedItem }) => {
                    const picked = selectedItem as PickedItem | null;
                    field.handleChange(picked?.value ?? '');
                    form.setFieldValue(
                      `${prefix}.configurableId`,
                      picked?.id ?? null
                    );
                    if (picked?.id && entryId) {
                      fetchGeometry({
                        entryId,
                        type: 'unit',
                        configurableId: picked.id,
                      }).catch(() => {
                        field.handleChange('');
                        form.setFieldValue(`${prefix}.configurableId`, null);
                      });
                    } else if (entryId) {
                      forgetGeometry(entryId);
                    }
                  }}
                  onInputChange={(text) => debouncedUnitQuery(text)}
                />
              )}
            </form.Field>
            {unitSearchState.state === 'loading' && (
              <InlineLoading description="" className={styles.spinner} />
            )}
          </div>
        )}
      </form.Subscribe>
    </div>
  );
}
